/**
 * @file AdditivesModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-14
 * @modified 2026-09-13
 * @description Modal for adding/editing additives. Only code and name are
 *              required — suppliers frequently don't give us a cost or an
 *              inclusion rate, and forcing those fields only produced invented
 *              numbers. Codes are unique per supplier, not globally.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './ContactModal.css';
import Input from './Input';
import AuditHistoryModal from './AuditHistoryModal';
import { useSlideOutButton } from './SlideOutButton';
import SearchableDropdown from './SearchableDropdown';

function AdditivesModal({ additive, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  // Slideout buttons
  useSlideOutButton();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    inclusion_rate: '',
    price: '',
    notes: '',
    supplier_account_id: null
  });

  useEffect(() => {
    // Prevent background scrolling
    document.body.classList.add('modal-open');
    const contentWrapper = document.querySelector('.content-wrapper');
    if (contentWrapper) {
      contentWrapper.classList.add('modal-open');
    }

    // Pre-populate form if editing
    if (additive) {
      setFormData({
        code: additive.code || '',
        name: additive.name || '',
        // `?? ''` rather than `|| ''`: a stored 0 is a real value (a free
        // additive), and `||` turned it into an empty field that then failed
        // validation — locking the row out of editing entirely.
        inclusion_rate: additive.inclusion_rate ?? '',
        price: additive.price ?? '',
        notes: additive.notes || '',
        supplier_account_id: additive.supplier_account_id || null
      });
    }

    // Load supplier accounts for the picker. /api/contacts/search returns
    // { contacts: [...], pagination: {...} } — the dropdown was previously
    // reading json.data which never matches, so the list stayed empty.
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=500`, {
      credentials: 'include'
    })
      .then(r => r.json())
      .then(json => {
        const rows = Array.isArray(json?.contacts) ? json.contacts
                   : Array.isArray(json?.data) ? json.data
                   : Array.isArray(json) ? json : [];
        setSuppliers(rows);
      })
      .catch(err => console.error('Failed to load suppliers for additive picker:', err));

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) {
        contentWrapper.classList.remove('modal-open');
      }
    };
  }, [additive]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Only code and name are required. Inclusion rate and price are routinely
      // not supplied to us, and demanding a number there only ever got an
      // invented one typed in.
      if (!formData.code || !formData.name) {
        setError('Code and name are required');
        setLoading(false);
        return;
      }

      const url = additive
        ? `${API_URL}/api/additives/${additive.id}`
        : `${API_URL}/api/additives`;

      const method = additive ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save additive');
      }

      onSave();
    } catch (error) {
      console.error('Error saving additive:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content modal-sm additives-modal ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{margin: "0"}}>{additive ? 'Edit Additive' : 'Add New Additive'}</h2>
          <div style={{display: "flex"}}>
            {additive && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowHistory(true)}
                title="View History"
              >
                <span className="material-symbols-rounded">history</span>
              </button>
            )}
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{padding: "var(--spacing-xl"}}>
            {error && (
              <div className="error-message">
                <span className="material-symbols-rounded">error</span>
                {error}
              </div>
            )}

            <div className="form-row additives-grid" style={{ gridTemplateColumns: '75px 1fr' }}>
              <div className="form-group">
                <label>Code <span className="required">*</span></label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  placeholder="e.g., 060"
                  required
                  disabled={!!additive} // Don't allow code changes when editing
                />
              </div>

              <div className="form-group">
                <label>Name <span className="required">*</span></label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., HF Booster"
                  required
                />
              </div>
            </div>

            <div className="form-row additives-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className="form-group">
                <label>Inclusion Rate (%)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.inclusion_rate}
                  onChange={(e) => handleChange('inclusion_rate', e.target.value)}
                  placeholder="Leave blank if not supplied"
                />
              </div>

              <div className="form-group">
                <label>Price per Tonne (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="Leave blank if not supplied"
                />
              </div>

            </div>

            <div className="form-row additives-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="form-group">
                <label>Supplier</label>
                <SearchableDropdown
                  options={[
                    { value: null, label: 'Any supplier (global)' },
                    ...suppliers.map(s => ({
                      value: s.id,
                      label: `${s.account_no ? `${s.account_no} - ` : ''}${s.company_name}`
                    }))
                  ]}
                  value={formData.supplier_account_id}
                  onChange={(v) => handleChange('supplier_account_id', v)}
                  placeholder="Select supplier (optional)..."
                />
              </div>
            </div>

            <div className="form-row">
              <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: '1em', fontSize: '11px', display: 'block', margin: 'var(--spacing-sm) 0 var(--spacing-lg)' }}>
                Leave blank to make this additive available to every supplier. Additives assigned to one supplier are also available to others in the same supplier group.
              </span>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  className="form-control"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Additional information..."
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="submit"
              className="btn-slideout btn-slideout-primary"
              disabled={loading}
            >
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded ${loading ? 'spinning' : ''}`}>
                  {loading ? 'sync' : 'save'}
                </span>
              </div>
              <span className="btn-label">{loading ? 'Saving...' : additive ? 'Update' : 'Create'}</span>
            </button>
            <button data-read-safe type="button" className="btn-round btn-danger" onClick={handleClose} disabled={loading}>
              <div className="icon-wrapper">
                <span className="material-symbols-rounded">close</span>
              </div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
        </form>
      </div>

      {showHistory && additive && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="additive"
          entityId={additive.id}
          entityIdentifier={`${additive.code} - ${additive.name}`}
        />
      )}
    </div>,
    document.body
  );
}

export default AdditivesModal;
